<script lang="ts">
// Max horizontal span of the rendered waveform canvas. Exported (via
// this non-setup script block) so siblings that overlay the waveform
// — like VoiceMemoPage's progress line and its drag-to-seek pointer
// math — can stay aligned without duplicating the magic number.
export const WAVEFORM_MAX_WIDTH_PX = 600
</script>

<script lang="ts" setup>
import type { Attachment } from '@consumer/types/serializers'
import WaveSurfer from 'wavesurfer.js'
import { isDark, lighten } from '@consumer/helpers/color'
import { isiOS } from '@tiptap/core'

// Thin wrapper around WaveSurfer. Owns the canvas div and the
// WaveSurfer instance, exposes a small imperative API to the parent
// (`play`, `pause`, `setTime`, `getCurrentTime`), and forwards every
// playback event upward. The parent doesn't pass any reactive prop
// that this component reads, so it never re-renders during playback —
// the canvas is painted entirely by WaveSurfer outside Vue's reactivity
// tree. That's the whole point of pulling it into its own component:
// the parent (`VoiceMemoPage`) updates `currentTime` every animation
// frame, and we don't want that to ripple through to a re-render here.

withDefaults(defineProps<{
  voiceMemo: Attachment | string | null | undefined
}>(), {  })

const emit = defineEmits<{
  ready: [duration: number]
  timeupdate: [time: number]
  seeking: [time: number]
  dragstart: []
  drag: [relativeX: number]
  dragend: []
  finish: []
  play: []
  pause: []
}>()

const containerEl = ref<HTMLDivElement>()
let wavesurfer: WaveSurfer | null = null

function voiceMemoUrl (): string | null {
  if (!__props.voiceMemo) return null
  if (typeof __props.voiceMemo === 'string') return __props.voiceMemo
  if (__props.voiceMemo.signedId) {
    return `/rails/active_storage/blobs/proxy/${__props.voiceMemo.signedId}/${__props.voiceMemo.filename}`
  }
  return null
}

// Function ref so we can spin WaveSurfer up the instant the element
// mounts (the parent renders us inside a v-for + v-if chain, so by the
// time our own onMounted fires the element may already be there). We
// defer one tick before init so the cascade has fully resolved before
// we read inherited custom properties — function refs run synchronously
// during Vue's patch, which can be earlier than the moment the browser
// exposes ancestor inline styles via `getComputedStyle()`.
function setContainerRef (el: unknown) {
  if (el instanceof HTMLDivElement) {
    containerEl.value = el
    if (!wavesurfer) nextTick(initWavesurfer)
  }
  else {
    containerEl.value = undefined
  }
}

function initWavesurfer () {
  if (!containerEl.value) return
  const url = voiceMemoUrl()
  if (!url) return

  // Resolve themed colors from the cascade. `progressColor` (played
  // bars) is the admin's font color; `waveColor` (unplayed bars) is a
  // lightened version of either the page or the font color, depending
  // on whether the page is already light — see VoiceMemoPage for the
  // earlier discussion of why.
  const styles = getComputedStyle(containerEl.value)
  const fontColor = styles.getPropertyValue('--font-color').trim()
    || styles.getPropertyValue('--text-dark').trim()
    || '#4a3e3d'
  const pageColor = styles.getPropertyValue('--page-bg-color').trim()
    || styles.getPropertyValue('--text-light').trim()
    || '#4a3e3d'

  wavesurfer = WaveSurfer.create({
    container: containerEl.value,
    waveColor: isDark(pageColor) ? lighten(pageColor, 0.4) : lighten(fontColor, 0.5),
    progressColor: fontColor,
    cursorColor: 'transparent',
    cursorWidth: 0,
    barWidth: 5,
    barGap: 2,
    // address weird skip jumps on waveform progress close to every second mark.
    backend: isiOS() ? 'WebAudio' : 'MediaElement',
    barRadius: 100,
    height: 240,
    interact: true,
    dragToSeek: true,
  })

  wavesurfer.load(url)
  wavesurfer.on('ready', () => emit('ready', wavesurfer!.getDuration()))
  wavesurfer.on('timeupdate', time => emit('timeupdate', time))
  wavesurfer.on('seeking', time => emit('seeking', time))
  // `dragstart` / `drag` / `dragend` fire across the gesture's lifetime:
  // `dragstart` once on press, `drag` continuously with a 0–1
  // relative-x value, `dragend` once on release. The parent uses the
  // bookends to pause-on-start / resume-on-end so audio doesn't keep
  // playing past the new seek position while the recipient is still
  // dragging.
  wavesurfer.on('dragstart', () => emit('dragstart'))
  wavesurfer.on('drag', relativeX => emit('drag', relativeX))
  wavesurfer.on('dragend', () => emit('dragend'))
  wavesurfer.on('finish', () => emit('finish'))
  wavesurfer.on('play', () => emit('play'))
  wavesurfer.on('pause', () => emit('pause'))
}

function play () { wavesurfer?.play() }
function pause () { wavesurfer?.pause() }
function setTime (t: number) { wavesurfer?.setTime(t) }
function getCurrentTime (): number { return wavesurfer?.getCurrentTime() ?? 0 }

defineExpose({ play, pause, setTime, getCurrentTime })

onBeforeUnmount(() => {
  wavesurfer?.destroy()
  wavesurfer = null
})
</script>

<template>
  <div
    :ref="setContainerRef"
    class="waveform"
    :style="{ maxWidth: `${WAVEFORM_MAX_WIDTH_PX}px` }"
    @pointerdown.stop
    @click.stop
  />
</template>

<style scoped lang="scss">
.waveform {
  // `max-width` lives on the element as an inline style sourced from
  // the `WAVEFORM_MAX_WIDTH_PX` export above — keeps this single value
  // shared with `VoiceMemoPage`'s line-position math.
  height: 240px;
  margin: 0 auto;
  width: 100%;

  :deep(.progress) {
    opacity: 0.7;
  }
}
</style>
